"use client";

import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateChatDetailQueries } from "@utils/chatDetailInvalidateQueries";
import type { SaveGoalArgs } from "@components/Chat/InteractiveTool/types";
import { planQueryKeys } from "@/src/hooks/plan/planQueryKeys";
import {
  useGoalActivationFlow,
  type GoalActivationOutcome,
} from "./useGoalActivationFlow";
import { resolveGoalIdForActivate } from "./resolveGoalIdForActivate";

export type GoalActivateRunResult = GoalActivationOutcome;

export type RunGoalActivateParams = {
  saveGoalArgs: SaveGoalArgs;
  goalId?: string;
  /** Chat 会话 id；ACTIVE 后刷新工具消息的详情。 */
  chatId?: string;
};

/**
 * Chat `save_goal` card activation adapter.
 *
 * `save_goal` has already persisted a PLANNED goal and its USER trackers. This adapter
 * resolves that authoritative goal id and delegates only to `/api/goals/{goalId}/activate`.
 */
export function useGoalActivatePaywallGate() {
  const queryClient = useQueryClient();
  const { activate, navigateToGoal } = useGoalActivationFlow();
  const [isActivating, setIsActivating] = useState(false);

  const runActivate = useCallback(
    async (params: RunGoalActivateParams): Promise<GoalActivateRunResult> => {
      const goalId = resolveGoalIdForActivate(
        params.saveGoalArgs,
        params.goalId,
      );
      if (!goalId) {
        return { outcome: "error", reason: "activation_failed" };
      }
      setIsActivating(true);
      try {
        const result = await activate({
          goalId,
          goalTitle: params.saveGoalArgs.title,
          source: "chat",
          navigateOnSuccess: false,
        });
        if (result.outcome === "activated") {
          queryClient.removeQueries({
            queryKey: planQueryKeys.diagnosisGoals(),
          });
          if (params.chatId?.trim()) {
            invalidateChatDetailQueries(queryClient, params.chatId.trim());
          }
        }
        return result;
      } finally {
        setIsActivating(false);
      }
    },
    [activate, queryClient],
  );

  return { runActivate, isActivating, navigateToGoal };
}
